import { Link } from "react-router-dom";
import { Star, MapPin, ArrowRight, Award } from "lucide-react";
import { Card, CardContent, CardFooter, CardHeader } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Shop } from "@/src/types";
import { MALL_ZONES } from "@/src/constants";

interface ShopCardProps {
  shop: Shop;
  key?: string | number;
}

export function ShopCard({ shop }: ShopCardProps) {
  return (
    <Card className="overflow-hidden group hover:shadow-lg transition-all duration-300">
      <div className="relative aspect-video overflow-hidden">
        <img
          src={shop.bannerURL || `https://picsum.photos/seed/${shop.id}/800/450`}
          alt={shop.name}
          className="object-cover w-full h-full group-hover:scale-105 transition-transform duration-500"
          referrerPolicy="no-referrer"
        />
        <div className="absolute top-2 right-2 flex gap-2">
          {shop.featured && (
            <Badge className="bg-primary text-primary-foreground">Destacado</Badge>
          )}
          {shop.isClubAffiliated && (
            <Badge className="bg-gradient-to-r from-amber-400 to-amber-600 text-white border-none gap-1">
              <Award className="h-3 w-3" /> Club
            </Badge>
          )}
          {shop.mallZone && shop.mallZone !== 'otros' && (
            <Badge variant="outline" className="bg-white/80 backdrop-blur-sm border-primary/20 text-primary">
              {MALL_ZONES.find(z => z.id === shop.mallZone)?.label}
            </Badge>
          )}
        </div>
      </div>
      <CardHeader className="p-4 flex flex-row items-center gap-4">
        <div className="h-12 w-12 rounded-full border-2 border-background overflow-hidden -mt-10 z-10 bg-white shadow-md">
          <img
            src={shop.logoURL || `https://picsum.photos/seed/${shop.name}/100/100`}
            alt={shop.name}
            className="h-full w-full object-cover"
            referrerPolicy="no-referrer"
          />
        </div>
        <div className="flex-1">
          <h3 className="font-bold text-lg leading-none">{shop.name}</h3>
          <div className="flex items-center text-xs text-muted-foreground mt-1">
            <MapPin className="h-3 w-3 mr-1" />
            {shop.location.city}, {shop.location.country}
          </div>
        </div>
      </CardHeader>
      <CardContent className="p-4 pt-0">
        <p className="text-sm text-muted-foreground line-clamp-2 min-h-[2.5rem]">
          {shop.description}
        </p>
        <div className="flex flex-wrap gap-1 mt-3">
          {shop.categories.slice(0, 3).map((cat) => (
            <Badge key={cat} variant="secondary" className="text-[10px] px-1.5 py-0">
              {cat}
            </Badge>
          ))}
        </div>
      </CardContent>
      <CardFooter className="p-4 pt-0 flex items-center justify-between">
        <div className="flex items-center text-sm font-medium">
          <Star className="h-4 w-4 text-yellow-500 fill-yellow-500 mr-1" />
          {shop.rating.toFixed(1)}
          <span className="text-muted-foreground text-xs ml-1">({shop.reviewCount})</span>
        </div>
        <Link to={`/shop/${shop.id}`}>
          <Button variant="ghost" size="sm" className="group/btn">
            Visitar
            <ArrowRight className="ml-2 h-4 w-4 group-hover/btn:translate-x-1 transition-transform" />
          </Button>
        </Link>
      </CardFooter>
    </Card>
  );
}
